import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
    Plus, 
    ClipboardCheck, 
    Lock, 
    ShieldAlert, 
    AlertCircle,
    Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { getRubrics } from '@/api/rubrics/api';
import type { Rubric } from '@/api/rubrics/types';
import RubricDisplay from './components/RubricDisplay';
import { useAuth } from '@/context/AuthContext';
import { 
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { containerVariants, itemVariants } from '@/components/animations';
import { RubricSkeleton } from './components/RubricSkeleton';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { SiteHeader } from '@/components/site-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Combobox } from '@/components/ui/combobox';

const RubricsPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [rubrics, setRubrics] = useState<Rubric[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const { user } = useAuth();
    const { error: showError } = useEnhancedToast();

    const canCreateRubric = user?.permissions?.rubrics === 'EDITOR';

    // Type filter options
    const typeOptions = [
        { value: 'all', label: 'All Types' },
        { value: 'inboundPhoneCall', label: 'Inbound Calls' },
        { value: 'outboundPhoneCall', label: 'Outbound Calls' }
    ];

    // Filter rubrics based on search and type
    const filteredRubrics = useMemo(() => {
        return rubrics.filter(rubric => {
            // Search filter
            const matchesSearch = rubric.name.toLowerCase().includes(searchQuery.toLowerCase());
            
            // Type filter
            const matchesType = typeFilter === 'all' || rubric.type === typeFilter;
            
            return matchesSearch && matchesType;
        });
    }, [rubrics, searchQuery, typeFilter]);

    const fetchRubrics = async () => {
        setIsLoading(true);
        try {
            const response = await getRubrics();
            if (response.error) {
                showError({
                    title: 'Failed to Load Rubrics',
                    description: response.error,
                    icon: AlertCircle
                });
            } else if (response.data) {
                setRubrics(response.data.data.rubrics);
            }
        } catch {
            showError({
                title: 'Error',
                description: 'Failed to fetch rubrics',
                icon: AlertCircle
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRubrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNewRubric = () => {
        if (canCreateRubric) {
            navigate('/dashboard/rubrics/new');
        }
    };

    const NewRubricButton = ({ size = "sm", className = "" }) => (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div>
                        <Button 
                            onClick={handleNewRubric} 
                            size={size as "sm" | "lg"} 
                            className={className}
                            disabled={!canCreateRubric}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            New Rubric
                            {!canCreateRubric && <Lock className="w-3.5 h-3.5 ml-2 opacity-70" />}
                        </Button>
                    </div>
                </TooltipTrigger>
                {!canCreateRubric && (
                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                        <ShieldAlert className="w-12 h-12 text-amber-500" />
                        <p>You need editor permissions to create rubrics. Contact your administrator for access.</p>
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );


    return (
        <>
            <SiteHeader 
                title="Rubrics"
                subtitle="Manage analytics rubrics and evaluation criteria."
            >
                {!isLoading && rubrics.length > 0 && (
                    <>
                        {/* Search Input */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search rubrics..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[200px] lg:w-[250px] pl-9"
                            />
                        </div>

                        {/* Type Filter */}
                        <Combobox
                            options={typeOptions}
                            value={typeFilter}
                            onValueChange={(value) => setTypeFilter(value || 'all')}
                            placeholder="Filter by type..."
                            searchPlaceholder="Search types..."
                            emptyMessage="No type found."
                            className="w-[160px]"
                        />

                        {/* New Rubric Button */}
                        <NewRubricButton />
                    </>
                )}
            </SiteHeader>
            
            <div className="flex-1 overflow-auto">
                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <RubricSkeleton />
                        </motion.div>
                    </motion.div>
                ) : rubrics.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        {filteredRubrics.length > 0 ? (
                            <motion.div 
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            >
                                {filteredRubrics.map((rubric) => (
                                    <RubricDisplay 
                                        key={rubric._id}
                                        rubric={rubric} 
                                        onEdit={() => navigate(`/dashboard/rubrics/${rubric._id}`)}
                                        variants={itemVariants}
                                    />
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                className="flex items-center justify-center"
                                style={{ height: 'calc(100vh - 200px)' }}
                            >
                                <div className="text-center">
                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                        <Search className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-lg font-semibold mb-2">No Matching Rubrics</h3>
                                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                        We couldn't find any rubrics matching your current filters.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setTypeFilter('all');
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                ) : (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState
                                icon={ClipboardCheck}
                                title="No Rubrics Yet"
                                description="Create rubrics to define evaluation criteria for your analytics."
                                action={{
                                    label: "New Rubric",
                                    onClick: handleNewRubric,
                                    disabled: !canCreateRubric,
                                    disabledReason: "You need editor permissions to create rubrics. Contact your administrator for access.",
                                }}
                            />
                        </motion.div>
                    </div>
                )}
            </div>
        </>
    );
};

export default RubricsPage; 